import React, { useCallback, useEffect, useState } from "react";
import PrimaryButton from "../../../components/generic/buttons/PrimaryButton";
import { Card, Space, Spin, Tooltip } from "antd";
import { ListItem, ListItemAvatar, Typography } from "@mui/material";
import NoDataMessageComponent from "../../../components/generic/nodatamessage/NoDataMessageComponent";
import apiService from "../../../service/apiservice/apiService";
import { useGetOrgIdentifier } from "../../generic/hooks/useGetOrgIdentifier";
import CustomModal from "../../../components/generic/custommodal/CustomModal";
import SecondaryButton from "../../../components/generic/buttons/SecondaryButton";
import SmartIcon from "../../../components/generic/genericIcon/SmartIcon";
import { useRecoilValue } from "recoil";
import { officeLabel } from "../../../components/recoil/officeLabelRecoil";
import AddEditTeamForm from "./AddEditTeamForm";
import { currentConfiguration } from "../../../service/memory/recoil/configurationstore";
import ConfigurationConstants from "../../utils/configurationconstants";

const TeamSettings = () => {
  const organizationIdentifier = useGetOrgIdentifier();
  const [teams, setTeams] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isModalVisible, setModalVisible] = useState(false);
  const [teamDetails, setTeamDetails] = useState(null);
  const [errors, setErrors] = useState({});
  const [isSubmit, setIsSubmit] = useState(false);
  const [formAction, setFormAction] = useState("");
  const label = useRecoilValue(officeLabel);
  const configuration = useRecoilValue(currentConfiguration);
  const [isOfficesAllowed, setIsOfficesAllowed] = useState(false);

  const trimIfString = (value) =>
    typeof value === "string" ? value.trim() : value;

  const handleClick = async (e) => {
    e.preventDefault();
    setErrors(validate(teamDetails));
    setIsSubmit(true);
  };
  const validate = (values) => {
    const errors = {};
    if (!trimIfString(values?.name)) {
      errors.name = "Name is required!";
    }
    if (isOfficesAllowed) {
      if (!Array.isArray(values?.offices) || values?.offices.length === 0) {
        errors.offices = "Office is required";
      }
    }
    return errors;
  };

  const addClick = () => {
    setTeamDetails(null);
    setFormAction("add");
    setModalVisible(true);
  };

  const editClick = async (id) => {
    const response = await apiService.get(
      `niramayafoundation/niramayaworkforce/api/v1/teams/${id}`,
    );
    setTeamDetails(response);
    setFormAction("edit");
    setModalVisible(true);
  };

  const getTeams = useCallback(async () => {
    setIsLoading(true);
    const endpoint = "niramayafoundation/niramayaworkforce/api/v1/teams";
    const params = {
      organizationIdentifier,
    };
    const response = await apiService.get(endpoint, params);
    setTeams(response);
    setIsLoading(false);
  }, [organizationIdentifier]);

  const handleSubmit = useCallback(async () => {
    setSubmitLoading(true);
    const payload = {
      organizationIdentifier,
      name: trimIfString(teamDetails?.name),
      medicareProviderNumber: trimIfString(teamDetails?.medicareProviderNumber),
      nationalProviderIdentifier: trimIfString(
        teamDetails?.nationalProviderIdentifier,
      ),
      address: trimIfString(
        teamDetails?.completeAddress || teamDetails?.address,
      ),
      streetAddress: trimIfString(teamDetails?.streetAddress),
      addressLocality: trimIfString(teamDetails?.addressLocality),
      addressRegion: trimIfString(teamDetails?.addressRegion),
      postalCode: trimIfString(teamDetails?.postalCode),
      addressCountry: trimIfString(teamDetails?.addressCountry),
      notes: trimIfString(teamDetails?.notes),
      offices: teamDetails?.offices,
    };
    const url =
      formAction === "edit"
        ? `niramayafoundation/niramayaworkforce/api/v1/teams/${teamDetails?.id}`
        : "niramayafoundation/niramayaworkforce/api/v1/teams";
    const method = formAction === "edit" ? "put" : "post";
    const successMessage =
      formAction === "edit"
        ? `${label} updated successfully`
        : `Added ${label} successfully`;
    const errorMessage =
      formAction === "edit"
        ? `Error while updating ${label}`
        : `Error while adding ${label}`;

    const response = await apiService[method](
      url,
      payload,
      successMessage,
      errorMessage,
    );

    if (response?.id) {
      setIsSubmit(false);
      setModalVisible(false);
      getTeams();
      setTeamDetails(null);
    }
    setSubmitLoading(false);
  }, [
    formAction,
    getTeams,
    label,
    organizationIdentifier,
    teamDetails?.address,
    teamDetails?.addressCountry,
    teamDetails?.addressLocality,
    teamDetails?.addressRegion,
    teamDetails?.completeAddress,
    teamDetails?.id,
    teamDetails?.medicareProviderNumber,
    teamDetails?.name,
    teamDetails?.nationalProviderIdentifier,
    teamDetails?.notes,
    teamDetails?.offices,
    teamDetails?.postalCode,
    teamDetails?.streetAddress,
  ]);

  useEffect(() => {
    if (Object.keys(errors).length === 0 && isSubmit) {
      handleSubmit();
    }
  }, [errors, handleSubmit, isSubmit]);

  useEffect(() => {
    getTeams();
  }, [getTeams]);

  useEffect(() => {
    const configurationOffices =
      configuration[ConfigurationConstants.FEATUREACCESS_OFFICE_ADD];
    setIsOfficesAllowed(configurationOffices?.visibility);
  }, [configuration]);

  const customModelButtons = (
    <>
      <SecondaryButton
        buttonText={"Cancel"}
        handleOnClick={() => setModalVisible(false)}
      />
      <PrimaryButton
        buttonText={formAction === "edit" ? "Edit" : "Add"}
        handleOnClick={handleClick}
        loading={submitLoading}
      />
    </>
  );

  return (
    <>
      <Card
        className="services-card"
        title={
          <>
            <div className="preference-card-title">
              <strong className="services-card-title-font">
                {label ? `${label}s` : "Teams"}
              </strong>
              <PrimaryButton
                buttonText={`Create New ${label}`}
                handleOnClick={addClick}
              />
            </div>
          </>
        }
      >
        {isLoading ? (
          <Spin className="spinnerCenter" />
        ) : teams && teams?.length ? (
          teams?.map((office) => (
            <ListItem className="services-listitem" key={office?.id}>
              <Typography className="services-listitem-font">
                {office?.name}
              </Typography>
              <ListItemAvatar className="services-list-item-action">
                <Space size="middle">
                  <Tooltip title="Edit" placement="bottom">
                    <>
                      <SmartIcon
                        iconKey={"edit"}
                        width="20"
                        height="20"
                        handleIconClick={() => editClick(office?.id)}
                      />
                    </>
                  </Tooltip>
                  {/* <Tooltip title="Delete" placement="bottom">
                    <>
                      <SmartIcon
                        iconKey={"delete"}
                        width="20"
                        height="20"
                      />
                    </>
                  </Tooltip> */}
                </Space>
              </ListItemAvatar>
            </ListItem>
          ))
        ) : (
          <NoDataMessageComponent text="No data available" />
        )}
      </Card>

      {isModalVisible && (
        <CustomModal
          closable={true}
          isModalOpen={isModalVisible}
          footer={[customModelButtons]}
          closeModal={setModalVisible}
          title={
            formAction === "edit"
              ? `Edit ${label} Details`
              : `Create New ${label}`
          }
        >
          <AddEditTeamForm
            teamDetails={teamDetails}
            setTeamDetails={setTeamDetails}
            errors={errors}
            label={label}
            isOfficesAllowed={isOfficesAllowed}
          />
        </CustomModal>
      )}
    </>
  );
};

export default TeamSettings;
